import { Drawer, Dropdown } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import CustomAvatar from "../../../Core/CommonV2/CustomAvatar";
import { CrossIcon, CrossIconLarge, CrossIconSecondary } from "../../../Core/svgV2/CrossIcon";
import { EditIcon, EditV3Icon } from "../../../Core/svgV2/EditIcon";
import { PencilEditIcon } from "../../../Core/svgV3/PencilEditIcon";
import { DotsVerticalIcon } from "../../../Core/svgV2/DotsVertical";
import dayjs from "dayjs";
import { useAppDispatch, useAppSelector } from "../../../Core/redux/hooks";
import {
  updateConversationsMultiple,
  updateThreadForUser,
} from "../../../Core/redux/slices/conversations.slice";
import {
  muteThread,
  manageMembers,
  getThreadsMembers,
} from "../../../Core/redux/api/v3/conversationAPI";
import { SearchElongatedIcon } from "../../../Core/svgV2/SearchIcon";
import { PastRequestIcon } from "../../../Core/svgV2/PastRequestIcon";
import StarIcon from "../../../Core/svgV3/StarIcon";
import SpeakerMuteIcon from "../../../Core/svgV3/SpeakerMuteIcon";
import { SpeakerWaveFilledIcon } from "../../../Core/svgV3/SpeakerWaveFilledIcon";
import { TwoPersonFilledIcon } from "../../../Core/svgV2/TwoPersonFilledIcon";
import { LeaveGroupIcon } from "../../../Core/svgV2/LeaveGroupIcon";
import { DeleteIcon } from "../../../Core/svgV2/DeleteIcon";
import { AddMemberIcon } from "../../../Core/svgV3/AddMemberIcon";

const MEMBER_PREVIEW_COUNT = 5;
const MEMBER_LIST_MAX_HEIGHT = 280;

const PAGE_BG = "#EDF3F9";
const PAGE_BORDER = "#DDE5ED";
const SectionDivider = () => <div className="h-2 w-full flex-shrink-0" />;

const AdminPill = () => (
  <span
    className=" px-2 py-0.5  flex-shrink-0 font-light"
    style={{ fontSize: "14px", color: "#98A2B3" }}
  >
    Admin
  </span>
);

const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

const QuickActionButton = ({ onPress, icon, label, disabled = false }) => (
  <button
    onClick={disabled ? undefined : onPress}
    disabled={disabled}
    aria-disabled={disabled}
    className={`flex-1 h-[72px] rounded-[10px] text-black border border-[#EDF3F9] bg-[#F9FBFE] flex flex-col items-center justify-center gap-1 transition-colors ${disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50"
      }`}
  >
    {icon}
    <span className="out-500-12 text-black whitespace-nowrap leading-[18px]">{label}</span>
  </button>
);

// Figma "state-layer" row: 318 Fill x 56 Fill, padding top/bottom 8px,
// left 16px, right 24px, gap 16px (horizontal flow).
// canRemove/onRemove: admin-only "..." menu with a single "Remove from
// group" action, shown on row hover (or while its own dropdown is open).
// No pin action here — this is the member list, not the message list.
const MemberRow = ({ member, isYou, isMemberAdmin, canRemove, onClick, onRemove }) => {
  const memberId = member.userId ?? member.id;
  const [dropdownOpen, setDropdownOpen] = useState(false);

  return (
    <div className="group flex items-center gap-x-4 pl-4 pr-6 py-2 w-full hover:bg-gray-50 transition-colors">
      <button
        onClick={onClick}
        className="flex items-center gap-x-4 flex-1 min-w-0 text-left"
      >
        <CustomAvatar
          title={`${member.firstname} ${member.lastname}`}
          src={member.imgSrc}
          size={40}
          whiteText
          fontSize={"14px"}
          userId={memberId}
          color={member.profilePictureColorCode}
        />
        <div className="min-w-0 flex-1 flex items-center justify-between gap-x-2">
          <p className="out-600-16 text-[#1D1B20] truncate">
            {isYou ? "You" : `${member.firstname} ${member.lastname}`}
          </p>
          {isMemberAdmin && <AdminPill />}
        </div>
      </button>

      {canRemove && (
        <div
          className={`flex-shrink-0 w-4 h-4 flex items-center justify-center ${dropdownOpen ? "block" : "hidden group-hover:block"
            }`}
          onClick={(e) => e.stopPropagation()}
        >
          <Dropdown
            onOpenChange={setDropdownOpen}
            trigger={["click"]}
            dropdownRender={(menu) => <div className="w-[180px]">{menu}</div>}
            menu={{
              items: [
                {
                  key: "remove",
                  label: (
                    <div
                      className="text-error-600 out-300-14"
                      onClick={() => onRemove(member)}
                    >
                      Remove from group
                    </div>
                  ),
                },
              ],
            }}
          >
            <button className="flex items-center justify-center w-4 h-4">
              <DotsVerticalIcon className="text-gray-400 w-4 h-4" />
            </button>
          </Dropdown>
        </div>
      )}
    </div>
  );
};

const GroupDetailsDrawer = ({ open = false, onClose, details, onSearchClick }) => {
  const dispatch = useAppDispatch();
  const { threadMembers } = useAppSelector((state) => state.conversations);
  const { user } = useAppSelector((state) => state.userDetails);
  const { tenantDetails } = useAppSelector((state) => state.tenant);
  useEffect(() => {
    console.log('threadMembers raw:', threadMembers);
  }, [threadMembers]);
  const threadId = details?.threadId;

  const [showAllMembers, setShowAllMembers] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState(null);

  useEffect(() => {
    setShowAllMembers(false);
  }, [threadId, open]);

  const threadMedia = useAppSelector((state) =>
    threadId ? state.conversations.threadMedia?.[String(threadId)] : null
  );
  const liveThread = useAppSelector((state) =>
    threadId
      ? state.conversations.threads.find((t) => String(t.threadId) === String(threadId))
      : null
  );

  const isMuted = (liveThread ? liveThread.isMute : details?.isMute) == 1;

  const createdById =
    details?.createdById ?? details?.created_by ?? details?.admin ?? null;
  const isAdmin =
    createdById != null && String(createdById) === user.id.toString();

  const sharedFiles = useMemo(() => {
    if (!threadMedia) return [];
    return [...(threadMedia.media || []), ...(threadMedia.documents || [])];
  }, [threadMedia]);

  const sortedMembers = useMemo(() => {
    if (!threadMembers) return [];
    const you = threadMembers.filter(
      (m) => String(m.userId ?? m.id) === String(user.id)
    );
    const others = threadMembers.filter(
      (m) => String(m.userId ?? m.id) !== String(user.id)
    );
    return [...you, ...others];
  }, [threadMembers, user.id]);

  const memberCount = threadMembers?.length ?? 0;
  const hasMoreMembers = sortedMembers.length > MEMBER_PREVIEW_COUNT;
  const visibleMembers = showAllMembers
    ? sortedMembers
    : sortedMembers.slice(0, MEMBER_PREVIEW_COUNT);

  const handleOpenSharedFiles = () => {
    dispatch(updateConversationsMultiple([{ key: "sharedFilesDrawer", value: true }]));
  };

  const handleOpenEditGroup = () => {
    dispatch(
      updateConversationsMultiple([
        { key: "editThread", value: true },
        { key: "editThreadDetails", value: details },
      ])
    );
  };

  const handleOpenAddMembers = () => {
    dispatch(
      updateConversationsMultiple([
        { key: "addMembersModal", value: { visible: true, data: details } },
      ])
    );
  };

  // Admin-only. Fires manage-members-v3 with userIdToRemove, matching the
  // same object shape (userId/firstname/lastname) manageMembers already
  // expects for userIdToAdd — the backend destructures both the same way.
  const handleRemoveMember = async (member) => {
    const memberId = member.userId ?? member.id;
    if (removingMemberId) return;

    setRemovingMemberId(memberId);
    try {
      const response = await manageMembers({
        groupId: threadId,
        userIdToRemove: [
          {
            userId: memberId,
            firstname: member.firstname,
            lastname: member.lastname,
          },
        ],
        senderName: `${user.firstName} ${user.lastName}`.trim(),
      });

      if (response) {
        dispatch(getThreadsMembers({ threadId }));
      }
    } catch (e) {
      console.error("GroupDetailsDrawer remove member error:", e);
    } finally {
      setRemovingMemberId(null);
    }
  };

  const handleMuteChat = async () => {
    const nextIsMute = +!isMuted;

    dispatch(
      updateThreadForUser({
        threadId,
        isMute: nextIsMute,
        isThread: threadId,
      })
    );

    try {
      await muteThread({ chatId: threadId });
    } catch (e) {
      console.error("mute toggle failed:", e);
      dispatch(
        updateThreadForUser({
          threadId,
          isMute: isMuted ? 1 : 0,
          isThread: threadId,
        })
      );
    }
  };

  const handleSearchClick = () => {
    onSearchClick?.();
    onClose?.();
  };

  const handleLeaveGroup = () => {
    dispatch(
      updateConversationsMultiple([
        { key: "leaveThread", value: true },
        { key: "leaveThreadDetails", value: details },
      ])
    );
    onClose();
  };

  const handleDeleteGroup = () => {
    dispatch(
      updateConversationsMultiple([
        { key: "deleteThread", value: true },
        { key: "deleteThreadDetails", value: details },
      ])
    );
    onClose();
  };

  const attachmentCount = sharedFiles.length;

  return (
    <Drawer
      title={null}
      closable={false}
      placement="right"
      open={open}
      width={"318px"}
      onClose={onClose}
      styles={{ body: { padding: 0 } }}
    >
      <div
        className="flex flex-col h-full relative overflow-x-hidden border-l"
        style={{ borderColor: PAGE_BORDER }}
      >
        <button onClick={onClose} className="absolute top-4 left-4 z-10">
          <CrossIconSecondary width={28} height={28} />
        </button>

        {isAdmin && (
          <button onClick={handleOpenEditGroup} className="absolute top-4 right-4 z-10">
            <EditV3Icon className="text-primary-600" width={28} height={28} />
          </button>
        )}

        {/* Base layer is Gray/100 — matches the Figma outer frame. White
            sections float on top of it full-width; the grey only shows
            through in the SectionDivider gaps and in the footer, which
            intentionally has no white background. Single vertical
            scroll only — overflow-x-hidden guards against anything
            (long strings, oversized icons) pushing this sideways. */}
        <div
          className="flex flex-col flex-1 overflow-y-auto overflow-x-hidden w-full max-w-full"
          style={{ backgroundColor: PAGE_BG }}
        >
          {/* Identity + quick actions — one white section per Figma
              (Frame 122589 is a single 318 Fill x 342 Hug white frame
              containing the avatar/name/count AND the actions row —
              not two separate pieces floating on the grey page bg). */}
          <div className="bg-[#F9FBFE] w-full">
            {/* Identity */}
            <div className="px-4 pt-12 flex w-full justify-center flex-col items-center">
              <CustomAvatar
                size={120}
                title={details?.threadName ?? details?.name}
                src={details?.threadProfilePicture ?? details?.groupProfilePicture}
                whiteText
                fontSize="40px"
                color={details?.threadProfilePictureColorCode}
              />
              <p className="out-600-24 text-[#292927] mt-2.5 text-center">
                {details?.threadName ?? details?.name}
              </p>
              <p className="out-300-16 mt-1" style={{ fontSize: "16px", color: "#475467" }}>
                {memberCount} member{memberCount === 1 ? "" : "s"}
              </p>
            </div>

            {/* Quick actions */}
            <div className="px-4 pb-2 pt-5 flex items-stretch justify-between gap-x-2">
              <QuickActionButton
                icon={<PastRequestIcon className="w-5 h-5" />}
                label="Requests"
                disabled
              />
              <QuickActionButton
                icon={<StarIcon filled className="text-yellow-400" width={24} height={24} />}
                label="Starred"
                onPress={() => {/* TODO: wire to starred-messages view once it exists on web */ }}
              />
              <QuickActionButton
                icon={<SearchElongatedIcon className="text-primary-600 w-6 h-6" />}
                label="Search"
                onPress={handleSearchClick}
              />
              <QuickActionButton
                icon={
                  isMuted ? (
                    <SpeakerMuteIcon className="text-primary-600 w-5 h-5" />
                  ) : (
                    <SpeakerWaveFilledIcon className="text-primary-600 w-6 h-5" />
                  )
                }
                label={isMuted ? "Unmute" : "Mute"}
                onPress={handleMuteChat}
              />
            </div>
          </div>

          <SectionDivider />

          {/* Shared files — full-width white section, no side inset */}
          <div className="bg-[#F9FBFE] px-4 pt-3 pb-2 w-full">
            <div className="flex items-center justify-between pb-2">
              <p className="out-600-14 text-black">Shared files ({attachmentCount})</p>
              {attachmentCount > 0 && (
                <button onClick={handleOpenSharedFiles} className="out-500-14 text-primary-600">
                  View all
                </button>
              )}
            </div>
            {attachmentCount > 0 ? (
              <div className="grid grid-cols-4 gap-2">
                {sharedFiles.slice(0, 4).map((file, i) => {
                  const isImage = file.mimeType?.includes("image") || file.type === "image";
                  const duration = formatDuration(file.duration);
                  return (
                    <button
                      key={i}
                      onClick={handleOpenSharedFiles}
                      className="relative w-full aspect-square rounded-[6px] overflow-hidden bg-gray-100"
                    >
                      {isImage ? (
                        <img src={file.url} className="w-full h-full object-cover" />
                      ) : (
                        <img
                          src="/images/v2/conversations/file.svg"
                          className="w-full h-full object-contain p-3"
                        />
                      )}
                      {duration && (
                        <span className="absolute bottom-1 right-1 text-white text-[10px] leading-none bg-black/50 rounded px-1 py-0.5">
                          {duration}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="out-300-14 text-black py-1">No files shared yet</p>
            )}
          </div>

          <SectionDivider />

          <div className="bg-[#F9FBFE] w-full">
            <div className="flex items-center justify-between px-4 pt-4 pb-6">
              <p className="out-600-14 text-black">{memberCount} Members</p>
              <button onClick={() => {/* TODO: wire in-member search */ }}>
                {/* <SearchElongatedIcon className="text-gray-400 w-[18px] h-[18px]" /> */}
              </button>
            </div>

            {isAdmin && (
              <button
                className="flex items-center gap-x-4 pl-4 pr-6 py-2 w-full text-left hover:bg-gray-50 transition-colors"
                onClick={handleOpenAddMembers}
              >
                <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center shrink-0">
                  <AddMemberIcon className="text-primary-600" />
                </div>
                <span className="out-600-14 text-primary-600">Add members</span>
              </button>
            )}

            <div
              style={showAllMembers ? { maxHeight: MEMBER_LIST_MAX_HEIGHT } : undefined}
              className={showAllMembers ? "overflow-y-auto overflow-x-hidden conv-sidebar-scroll" : ""}
            >
              {visibleMembers.map((member) => {
                const memberId = member.userId ?? member.id;
                const isYou = String(memberId) === String(user.id);
                const isMemberAdmin =
                  createdById != null && String(memberId) === String(createdById);
                return (
                  <MemberRow
                    key={memberId}
                    member={member}
                    isYou={isYou}
                    isMemberAdmin={isMemberAdmin}
                    canRemove={isAdmin && !isMemberAdmin}
                    onClick={() => {/* TODO: open that member's UserDetailsDrawer */ }}
                    onRemove={handleRemoveMember}
                  />
                );
              })}
            </div>

            {hasMoreMembers && (
              <button
                onClick={() => setShowAllMembers((v) => !v)}
                className="out-500-14 text-primary-700 px-4 py-2 text-left w-full"
              >
                {showAllMembers ? "Show less" : `View all ${sortedMembers.length}`}
              </button>
            )}
          </div>

          <SectionDivider />

          {/* Leave/Delete — its own separate white row (Figma: 318 Fill x
              40 Hug, White, padding 10/14/10/14, gap 8), grey on both sides.
              Hover now tints error/50 instead of the generic gray-50 used
              everywhere else, since this is a destructive action. */}
          <div className="bg-[#F9FBFE] w-full">
            <button
              onClick={isAdmin ? handleDeleteGroup : handleLeaveGroup}
              className="flex items-center gap-2 w-full text-left pt-2.5 pr-3.5 pb-2.5 pl-3.5 hover:bg-error-50 transition-colors"
            >
              {isAdmin ? (
                <DeleteIcon className="text-error-600 w-5 h-5" />
              ) : (
                <LeaveGroupIcon className="text-error-600 w-5 h-5" />
              )}
              <span className="out-500-14 text-error-600">
                {isAdmin ? "Delete group" : "Leave group"}
              </span>
            </button>
          </div>

          <SectionDivider />

          {/* Footer — no white background here. It sits directly on the
              page's Gray/100, per Figma's outer frame color. */}
          <div className="px-3.5 pt-3 pb-3.5 flex flex-col gap-1 w-full max-w-full">
            <p
              className="font-light break-words"
              style={{ fontSize: "10px", lineHeight: "100%", color: "#667085" }}
            >
              Created on{" "}
              {details?.createdAt ? dayjs(details.createdAt).format("D MMM YYYY") : "-"}
            </p>
            <p
              className="font-light break-words"
              style={{ fontSize: "10px", lineHeight: "100%", color: "#667085" }}
            >
              {tenantDetails?.tenantName}
            </p>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default GroupDetailsDrawer;